import { FC, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { client, createCacheKey } from '../lib/sanityClient';
import { Link } from 'react-router-dom';
import ArtistNav from './ArtistNav';
import { useState } from 'react';

interface ArtistDetails {
  _id: string;
  name: string;
  photoUrl?: string;
  biography?: string;
  artworks: Array<{
    _id: string;
    title: string;
    imageUrl: string;
    slug: {
      current: string;
    };
  }>;
  exhibitions: Array<{
    _id: string;
    title: string;
    bannerImageUrl?: string;
    venue?: string;
    startDate?: string;
    endDate?: string;
    slug: string | {
      current: string;
    };
  }>;
  blogPosts: Array<{
    _id: string;
    title: string;
    imageUrl?: string;
    publishedDate?: string;
    slug: {
      current: string;
    };
  }>;
}

interface ArtistPageProps {
  hideOnMenu?: boolean;
}

const ArtistPage: FC<ArtistPageProps> = ({ hideOnMenu = false }) => {
  const { slug } = useParams<{ slug: string }>();
  
  // Ensure slug is defined before querying
  if (!slug) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="text-xl">Artist slug not found</div>
      </div>
    );
  }

  const { data: artist, isLoading, error } = useQuery<ArtistDetails>({
    queryKey: createCacheKey('artist-details', { slug }),
    queryFn: async () => {
      const query = `*[_type == "artist" && slug.current == $slug][0] {
        _id,
        name,
        "photoUrl": photo.asset->url,
        biography,
        "artworks": artworks[]->{
          _id,
          title,
          "imageUrl": image.asset->url,
          "slug": slug.current
        },
        "exhibitions": *[_type == "event" && references(^._id)] | order(startDate desc) {
          _id,
          title,
          "slug": slug.current,
          startDate,
          endDate,
          venue,
          "bannerImageUrl": bannerImage.asset->url
        },
        "blogPosts": *[_type == "blogPost" && references(^._id)] | order(publishedDate desc) {
          _id,
          title,
          "slug": slug.current,
          publishedDate,
          "imageUrl": mainImage.asset->url
        }
      }`;
      return client.fetch<ArtistDetails>(query, { slug });
    }
  });

  // Enhanced debugging
  console.log('Artist page rendering with slug:', slug);
  console.log('Artist data:', artist);
  console.log('Loading state:', isLoading);
  console.log('Error state:', error);
  
  // Debug exhibitions specifically
  if (artist) {
    console.log('Artist found:', artist.name);
    console.log('Exhibitions data:', artist.exhibitions);
  } else if (!isLoading) {
    console.log('Artist not found and not loading');
  }

  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="text-xl">Loading artist details...</div>
      </div>
    );
  }

  if (error) {
    console.error('Error fetching artist:', error);
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="text-xl text-red-600">Error loading artist details</div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="text-xl">Artist not found</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Artist Navigation */}
      <ArtistNav hideOnMenu={hideOnMenu} />
      
      <div className="mb-8">
        <h1 className="text-4xl font-book-antiqua font-bold mb-6 artist-name">{artist.name}</h1>
        {artist.biography && (
          <div className="text-gray-700 text-justify max-w-full biography-text">
            {artist.biography.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-2" style={{ marginBottom: '0.5em' }}>{paragraph}</p>
            ))}
          </div>
        )}
      </div>

      {/* Artworks Section - Horizontally Scrollable with Direct Links */}
      {artist.artworks && artist.artworks.length > 0 && (
        <section className="mb-8">
          <h2 className="text-3xl font-book-antiqua font-bold mb-4 section-header">Artworks</h2>
          <div className="flex overflow-x-auto pb-4 hide-scrollbar" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <div className="flex space-x-6">
              {artist.artworks.map((artwork) => (
                <div key={artwork._id} className="relative flex-none w-80">
                  <Link
                    to={`/works/${artwork.slug}`}
                    className="block group"
                  >
                    <div className="relative w-full h-80 overflow-hidden rounded-lg">
                      <img
                        src={artwork.imageUrl}
                        alt={artwork.title}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                      <h3 className="text-xl font-ogg font-light italic text-white artwork-title">{artwork.title}</h3>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Exhibitions Section - Visual Cards with Direct Links */}
      <section className="mb-8">
        <h2 className="text-2xl font-book-antiqua font-bold mb-4 section-header">Exhibitions</h2>
        {artist.exhibitions && artist.exhibitions.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {artist.exhibitions.map((exhibition) => (
              <Link
                key={exhibition._id}
                to={`/programme/${typeof exhibition.slug === 'string' ? exhibition.slug : (exhibition.slug && exhibition.slug.current ? exhibition.slug.current : '')}`}
                className="block group hover:shadow-md transition-shadow duration-300 rounded-lg border border-transparent hover:border-gray-200 overflow-hidden"
              >
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  {exhibition.bannerImageUrl ? (
                    <img 
                      src={exhibition.bannerImageUrl} 
                      alt={exhibition.title} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-500 text-sm">No Image Available</span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-ogg font-light not-italic text-lg group-hover:text-gray-700">{exhibition.title}</h3>
                  {exhibition.venue && (
                    <p className="text-sm text-gray-600 mt-1">{exhibition.venue}</p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No exhibitions found for this artist.</p>
          </div>
        )}
      </section>

      {/* Blog Posts Section - Visual Cards with Direct Links */}
      {artist.blogPosts && artist.blogPosts.length > 0 && (
        <section>
          <h2 className="text-2xl font-book-antiqua font-bold mb-4 section-header">Blog Posts</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {artist.blogPosts.map((post) => (
              <Link
                key={post._id}
                to={`/blog/${post.slug}`}
                className="block group hover:shadow-md transition-shadow duration-300 rounded-lg border border-transparent hover:border-gray-200 overflow-hidden"
              >
                <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
                  {post.imageUrl ? (
                    <img 
                      src={post.imageUrl} 
                      alt={post.title} 
                      className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                      <span className="text-gray-500 text-sm">No Image Available</span>
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <h3 className="font-ogg font-light not-italic text-lg group-hover:text-gray-700">{post.title}</h3>
                  {post.publishedDate && (
                    <p className="text-sm text-gray-600 mt-1">
                      {new Date(post.publishedDate).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

// Move the scrollbar styles to the component to ensure proper cleanup
interface ArtistPageWithStylesProps {
  hideOnMenu?: boolean;
}

const ArtistPageWithStyles: FC<ArtistPageWithStylesProps> = (props) => {
  // Add CSS for hiding scrollbars when component mounts
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = `
      .hide-scrollbar::-webkit-scrollbar {
        display: none;
      }
      .hide-scrollbar {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
    `;
    
    if (typeof document !== 'undefined') {
      document.head.appendChild(styleElement);
    }
    
    // Clean up the style element when component unmounts
    return () => {
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
    };
  }, []);
  
  return <ArtistPage {...props} />;
}

export default ArtistPageWithStyles;
